import React from 'react'

import { Box } from '@gousto-internal/citrus-react'
import PropTypes from 'prop-types'

import { PricePerPortionMessage } from 'components/PricePerPortionMessage'
import { useNumPortions } from 'hooks/basket/useNumPortions'
import { useBoxes } from 'hooks/menu-boxes/useBoxes'
import { useMaxRecipesForPortion } from 'hooks/menu-boxes/useMaxRecipesForPortion'
import { useMinRecipesForPortion } from 'hooks/menu-boxes/useMinRecipesForPortion'
import { TooManyRecipesWarning } from 'routes/Menu/components/TooManyRecipesWarning/TooManyRecipesWarning'
import { useNumRecipesOverBasketLimit } from 'routes/Menu/domains/basket'

import { BoxProgressMessage } from '../../../BoxProgressMessage'
import { BoxSummaryAlert } from '../../BoxSummaryAlert'

import css from '../Details.css'

const BoxProgressAlert = ({ numRecipes, showPricePerPortionMessage = true }) => {
  const { isBoxesLoading } = useBoxes()
  const { numPortions } = useNumPortions()
  const maxRecipesNum = useMaxRecipesForPortion(numPortions)
  const minRecipesNum = useMinRecipesForPortion(numPortions)
  const hasMaxRecipes = numRecipes >= maxRecipesNum
  const hasMinRecipes = numRecipes >= minRecipesNum
  const numRecipesOverLimit = useNumRecipesOverBasketLimit()

  /* It doesn't make sense to show basket progress if we don't know what the maximum allowed number of recipe
  for the chosen portion size is, so we return null until we get that information from the menu-service. */
  if (isBoxesLoading) {
    return null
  }

  if (numRecipesOverLimit) {
    return (
      <Box paddingBottom={4}>
        <TooManyRecipesWarning numRecipesOverLimit={numRecipesOverLimit} />
      </Box>
    )
  }

  if (!showPricePerPortionMessage) {
    if (hasMinRecipes) {
      return null
    } else {
      return (
        <Box paddingBottom={10}>
          <BoxSummaryAlert variant="info" data-testing="minimumRecipesBoxProgressAlert">
            <BoxProgressMessage
              numRecipes={numRecipes}
              showPricePerPortionMessaging={showPricePerPortionMessage}
            />
          </BoxSummaryAlert>
        </Box>
      )
    }
  }

  return (
    <div className={css.boxProgressAlertWithGap} data-testing="boxProgressAlert">
      <BoxSummaryAlert variant={hasMaxRecipes ? 'success' : 'info'}>
        <div className={css.boxProgressAlertCopy}>
          <PricePerPortionMessage />
          <BoxProgressMessage numRecipes={numRecipes} />
        </div>
      </BoxSummaryAlert>
    </div>
  )
}

BoxProgressAlert.propTypes = {
  numRecipes: PropTypes.number.isRequired,
  showPricePerPortionMessage: PropTypes.bool.isRequired,
}

export { BoxProgressAlert }
